import React, { createContext, ReactNode, useCallback, useContext } from 'react';
import { toast } from 'react-hot-toast';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface NotificationContextType {
  showNotification: (message: string, type?: NotificationType) => string;
  hideNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType>({
  showNotification: () => '',
  hideNotification: () => {},
});

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const showNotification = useCallback((message: string, type: NotificationType = 'info'): string => {
    switch (type) {
      case 'success':
        return toast.success(message);
      case 'error':
        return toast.error(message);
      case 'warning':
        return toast(message, { icon: '⚠️' });
      case 'info':
      default:
        return toast(message, { icon: 'ℹ️' });
    }
  }, []);

  const hideNotification = useCallback((id: string) => toast.dismiss(id), []);

  return (
    <NotificationContext.Provider value={{ showNotification, hideNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);

export default NotificationContext;
